import { Component, HostListener, Injector, OnDestroy, OnInit, effect, inject, runInInjectionContext } from '@angular/core';
import { AuthService } from './security/auth.service';
import { MenuItem, MessageService } from 'primeng/api';
import { SessionStorageService } from './services/session-storage.service';
import { NavigationEnd, Router, RouterModule, RouterOutlet } from '@angular/router';
import { ToolbarService } from './services/toolbar.service';
import { Customer } from './customer/customer.model';
import { Company } from './company/company.model';
import { EnterpriseFeatureService } from "./services/enterprise-features.service";
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AvatarModule } from "primeng/avatar";
import { CommonModule, DOCUMENT } from '@angular/common';
import { SwUpdate } from '@angular/service-worker';
import { catchError, filter, map, take } from 'rxjs/operators';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { Messaging, getToken, onMessage } from '@angular/fire/messaging';
import { Observable, Subscription, interval, of } from 'rxjs';
import { ProductivityService } from './productivity/productivity.service';
import { ProductivitySummary } from './productivity/productivity.model';
import { environment } from 'src/environments/environment';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { PermissionService } from './security/permission.service';
import { resolveAppMenuPermissions } from './security/app-menu-permissions';
import { AppMenuController } from './shared/page-layout/app-menu.controller';
import { AppMenuBuilderService } from './shared/page-layout/app-menu-builder.service';
import { AppNavigationService } from './shared/page-layout/app-navigation.service';
import { AiChatDrawerComponent } from './ai-chat/ai-chat-drawer.component';
import { AiStateContext } from './ai-chat/ai-chat.model';
import { GlobalSearchComponent } from './shared/page-layout/global-search/global-search.component';
import { GlobalSearchController } from './shared/page-layout/global-search/global-search.controller';
import {
    normalizeUrl
} from './shared/page-layout/app-menu.utils';
import {
    GlobalSearchViewGroup,
    buildPageGlobalSearchGroups,
    mergeGlobalSearchGroups
} from './shared/page-layout/global-search.utils';
import {
    GlobalSearchService
} from './services/global-search.service';
import { TenantScopeSelectorComponent } from './company/tenant-scope-selector/tenant-scope-selector.component';
import { TenantScopeService } from './company/tenant-scope.service';
import { TenantScope } from './company/tenant-access.model';
import { CompanyService } from './company/company.service';
import { LocaleService } from './i18n/locale.service';
import { BaseMasterDataService } from './services/base-master-data.service';
import { TranslocoPipe } from '@jsverse/transloco';

interface BeforeInstallPromptEvent extends Event {
    readonly platforms: string[];
    readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
    prompt(): Promise<void>;
}

const THEME_STORAGE_KEY = 'logilink-theme-preference';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    imports: [
        CommonModule,
        RouterOutlet,
        RouterModule,
        FontAwesomeModule,
        AvatarModule,
        ButtonModule,
        DrawerModule,
        MenuModule,
        TooltipModule,
        ToastModule,
        AiChatDrawerComponent,
        GlobalSearchComponent,
        TenantScopeSelectorComponent,
        TranslocoPipe
    ]
})
export class AppComponent implements OnInit, OnDestroy {

    private roles: string[] = [];
    isLoggedIn: boolean = false;
    customer: Customer | null = null;
    company: Company | null = null;
    returnUrl = '';
    username?: string;
    title = 'LogiLink';
    toolbarMenuItems: MenuItem[] = [];
    profileMenuItems: MenuItem[] = [];
    readonly menu = new AppMenuController();
    readonly globalSearch: GlobalSearchController;
    mobileSidebarVisible = false;
    desktopNavExpanded = false;
    isUpdateAvailable: boolean = false;
    isPwaInstalled = false;
    canInstallPwa = false;
    isDarkMode = false;
    currentUrl = '';
    readonly instanceTag = environment.instanceTag;

    // AI Chat drawer
    aiChatDrawerVisible = false;
    aiChatStateContext: AiStateContext = {};
    productivityLauncherOpen = false;
    authBootstrapInProgress = false;
    productivitySummary: ProductivitySummary = {
        unreadNotifications: 0,
        pendingTasks: 0,
        pendingIncidents: 0
    };

    email: string = '';
    password: string = '';

    private authService = inject(AuthService);
    private sessionStorageService = inject(SessionStorageService);
    private router = inject(Router);
    private toolbarService = inject(ToolbarService);
    private companyService = inject(CompanyService);
    private enterpriseFeatureService = inject(EnterpriseFeatureService);
    private swUpdate = inject(SwUpdate);
    private productivityService = inject(ProductivityService);
    private messageService = inject(MessageService);
    private permissionService = inject(PermissionService);
    private tenantScopeService = inject(TenantScopeService);
    private appNavigationService = inject(AppNavigationService);
    private appMenuBuilderService = inject(AppMenuBuilderService);
    private globalSearchService = inject(GlobalSearchService);
    private localeService = inject(LocaleService);
    readonly navigationDirection = this.localeService.direction;
    private baseMasterDataService = inject(BaseMasterDataService);
    private messaging = inject(Messaging, { optional: true });
    private injector = inject(Injector);
    private document = inject(DOCUMENT);
    private readonly googleAnalytics = inject(GoogleAnalyticsService);
    private productivitySummarySubscription?: Subscription;
    private productivityIntervalSubscription?: Subscription;
    private sessionHeartbeatSubscription?: Subscription;
    private messagingRegistered = false;
    private deferredInstallPrompt: BeforeInstallPromptEvent | null = null;
    private authBootstrapUserUid: string | null = null;
    private tenantAccessBootstrapUserUid: string | null = null;

    constructor() {
        this.currentUrl = this.router.url && this.router.url !== '/' ? this.router.url : window.location.pathname;
        this.initializeThemePreference();
        this.globalSearch = new GlobalSearchController({
            navigate: route => {
                this.router.navigateByUrl(route);
            },
            resolve: query => this.resolveGlobalSearch(query)
        });

        // Effect for Login Status Change
        effect(() => {
            const loggedIn = this.toolbarService.isLoggedIn();
            const user = this.sessionStorageService.getUser();
            const hasToken = !!this.sessionStorageService.getAccessToken();

            if (!user) {
                this.isLoggedIn = false;
                this.authBootstrapInProgress = false;
                this.authBootstrapUserUid = null;
                this.tenantAccessBootstrapUserUid = null;
                this.stopSessionHeartbeat();
                if (loggedIn) {
                    this.toolbarService.updateLoggedInStatus(false);
                }
                this.clearAnalyticsUserContext();
                if (!this.isAnonymousAllowedRoute()) {
                    this.router.navigate(['/login'], { queryParams: { returnUrl: window.location.href } });
                }
                return;
            }

            if (user && !hasToken) {
                this.isLoggedIn = false;
                this.bootstrapAuthenticatedSession(user);
                return;
            }

            if (user && (loggedIn || hasToken)) {
                this.isLoggedIn = true;
                this.authBootstrapInProgress = false;
                this.authBootstrapUserUid = null;
                this.roles = user.roles;
                this.username = user.username;
                this.bootstrapTenantAccess(user);
                this.startSessionHeartbeat();
                this.syncAnalyticsUserContext();
            }
        });

        // Menu labels and selected-language checkmark are derived from the locale signal.
        effect(() => {
            this.localeService.locale();
            if (this.profileMenuItems.length) {
                this.refreshAppMenus();
            }
        });

        // Keep the session master-data cache aligned with the selected API culture.
        // A number of legacy forms read countries from SessionStorageService.
        let loadedMasterCulture = '';
        effect(() => {
            const cultureCode = this.localeService.apiCultureCode();
            const loggedIn = this.toolbarService.isLoggedIn();
            if (!loggedIn || cultureCode === loadedMasterCulture) {
                return;
            }
            loadedMasterCulture = cultureCode;
            this.baseMasterDataService.getBaseMasterData(cultureCode).subscribe({
                next: response => {
                    if (response?.data) {
                        this.sessionStorageService.saveBaseMasterData(response.data);
                    }
                },
                error: () => {
                    // Existing cached master data remains usable if a language refresh fails.
                }
            });
        });

        // Effect for Customer Change
        effect(() => {
            const customer = this.toolbarService.customer();
            if (customer !== null) {
                this.customer = customer;
                const activeScope = this.tenantScopeService.getActiveScope();
                const selectedCompany = this.sessionStorageService.getCompany();
                if (activeScope?.type === 'COMPANY' && selectedCompany?.id === activeScope.companyId) {
                    this.company = selectedCompany;
                } else {
                    this.company = customer?.company ?? selectedCompany ?? null;
                }
                this.bootstrapProductivityTools();
                this.syncAnalyticsUserContext();
            } else if (this.sessionStorageService.getCustomer()) {
                this.customer = this.sessionStorageService.getCustomer()!;
                this.company = this.sessionStorageService.getCompany() ?? this.customer?.company;
                this.bootstrapProductivityTools();
                this.syncAnalyticsUserContext();
            }
        });
    }

    ngOnInit() {
        this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe(event => {
                this.currentUrl = event.urlAfterRedirects ?? this.router.url;
                this.mobileSidebarVisible = false;
                this.closeDesktopNav();
                this.menu.setCurrentUrl(this.currentUrl);
            });
        this.checkForUpdates();
        this.refreshPwaInstallState();
    }

    ngOnDestroy(): void {
        this.globalSearch.destroy();
        this.productivitySummarySubscription?.unsubscribe();
        this.productivityIntervalSubscription?.unsubscribe();
        this.sessionHeartbeatSubscription?.unsubscribe();
    }

    get userRoleLabel(): string {
        const customer = this.customer ?? this.sessionStorageService.getCustomer();
        const user = this.sessionStorageService.getUser();
        const roleNames = customer ? this.resolveRoleNames(customer, user) : this.roles;
        const primaryRoleName = roleNames.find(roleName => !!roleName?.trim());

        return primaryRoleName ?? customer?.corporateRole ?? this.localeService.translate('shell.signedIn');
    }

    isPublicEdgeToEdgeRoute(): boolean {
        return this.isAnonymousAllowedRoute();
    }

    private isAnonymousAllowedRoute(url: string = this.currentUrl): boolean {
        const normalizedUrl = normalizeUrl(url && url !== '/' ? url : window.location.pathname);
        return normalizedUrl.startsWith('/enterprise-subscription')
            || normalizedUrl.startsWith('/login')
            || normalizedUrl.startsWith('/posm/client-tracking')
            || normalizedUrl.startsWith('/track');
    }

    checkForUpdates() {
        if (this.swUpdate.isEnabled) {
            this.swUpdate.versionUpdates.pipe(
                filter(evt => evt.type === 'VERSION_READY')
            ).subscribe(() => {
                this.isUpdateAvailable = true;
            });
        }
    }

    @HostListener('window:beforeinstallprompt', ['$event'])
    handleBeforeInstallPrompt(event: Event): void {
        const installEvent = event as BeforeInstallPromptEvent;

        event.preventDefault();

        if (this.isPwaInstalled) {
            return;
        }

        this.deferredInstallPrompt = installEvent;
        this.canInstallPwa = true;
    }

    @HostListener('window:appinstalled')
    handleAppInstalled(): void {
        this.isPwaInstalled = true;
        this.canInstallPwa = false;
        this.deferredInstallPrompt = null;
    }

    updateApp() {
        this.swUpdate.activateUpdate().then(() => window.location.reload());
    }

    async promptInstallPwa(): Promise<void> {
        if (!this.deferredInstallPrompt || this.isPwaInstalled) {
            return;
        }

        const installPrompt = this.deferredInstallPrompt;
        this.deferredInstallPrompt = null;
        this.canInstallPwa = false;

        await installPrompt.prompt();
        const choiceResult = await installPrompt.userChoice;

        if (choiceResult.outcome === 'accepted') {
            this.isPwaInstalled = true;
        }
    }

    signUp() {
        this.authService.register(this.email, this.password);
        this.email = '';
        this.password = '';
    }

    signIn() {
        this.authService.login(this.email, this.password);
        this.email = '';
        this.password = '';
    }

    logout(): void {
        this.clearAnalyticsUserContext();
        this.stopSessionHeartbeat();
        this.sessionStorageService.signOut();
        this.toolbarService.updateLoggedInStatus(false);
        window.location.reload();
        this.authService.logout();
    }

    toggleDarkMode(): void {
        this.applyThemePreference(!this.isDarkMode);
    }

    themeToggleLabel(): string {
        return this.localeService.translate(this.isDarkMode ? 'shell.lightMode' : 'shell.darkMode');
    }

    private startSessionHeartbeat(): void {
        if (this.sessionHeartbeatSubscription || !this.sessionStorageService.getAccessToken()) {
            return;
        }
        this.sessionHeartbeatSubscription = interval(15000).subscribe(() => {
            this.authService.checkCurrentBrowserSession().subscribe({ error: () => undefined });
        });
    }

    private stopSessionHeartbeat(): void {
        this.sessionHeartbeatSubscription?.unsubscribe();
        this.sessionHeartbeatSubscription = undefined;
    }

    private bootstrapAuthenticatedSession(user: any): void {
        const userUid = user?.uid ?? null;
        if (this.authBootstrapInProgress && this.authBootstrapUserUid === userUid) {
            return;
        }

        this.authBootstrapInProgress = true;
        this.authBootstrapUserUid = userUid;
        this.stopSessionHeartbeat();

        this.authService.getCurrentUserAccessToken().pipe(take(1)).subscribe({
            next: accessToken => {
                this.sessionStorageService.saveAccessToken(accessToken, new Date().getTime());
                this.authService.activateCurrentBrowserSession().pipe(take(1)).subscribe({
                    next: () => {
                        this.authBootstrapInProgress = false;
                        this.authBootstrapUserUid = null;
                        this.toolbarService.updateLoggedInStatus(true);
                    },
                    error: () => this.resetAuthenticatedSession()
                });
            },
            error: () => this.resetAuthenticatedSession()
        });
    }

    private resetAuthenticatedSession(): void {
        this.authBootstrapInProgress = false;
        this.authBootstrapUserUid = null;
        this.stopSessionHeartbeat();
        this.sessionStorageService.signOut();
        this.toolbarService.updateLoggedInStatus(false);
        this.clearAnalyticsUserContext();
        if (!this.isAnonymousAllowedRoute()) {
            this.router.navigate(['/login'], { queryParams: { returnUrl: window.location.href } });
        }
    }

    private initializeThemePreference(): void {
        const storedPreference = this.readStoredThemePreference();
        const prefersDark = typeof window !== 'undefined'
            && typeof window.matchMedia === 'function'
            && window.matchMedia('(prefers-color-scheme: dark)').matches;
        this.applyThemePreference(storedPreference === 'dark' || (!storedPreference && prefersDark), false);
    }

    private applyThemePreference(enabled: boolean, persist = true): void {
        this.isDarkMode = enabled;
        this.document.body.classList.toggle('app-dark', enabled);
        this.document.documentElement.style.colorScheme = enabled ? 'dark' : 'light';

        if (!persist) {
            return;
        }

        try {
            window.localStorage.setItem(THEME_STORAGE_KEY, enabled ? 'dark' : 'light');
        } catch {
            // Ignore storage failures and keep the session-local theme state.
        }
    }

    private readStoredThemePreference(): 'light' | 'dark' | null {
        try {
            const value = window.localStorage.getItem(THEME_STORAGE_KEY);
            return value === 'dark' || value === 'light' ? value : null;
        } catch {
            return null;
        }
    }

    private syncAnalyticsUserContext(): void {
        const customer = this.customer ?? this.sessionStorageService.getCustomer();
        const company = this.company ?? this.sessionStorageService.getCompany() ?? customer?.company ?? null;
        const user = this.sessionStorageService.getUser();

        if (!customer || !user) {
            this.clearAnalyticsUserContext();
            return;
        }

        const roleNames = this.resolveRoleNames(customer, user);
        const providerIds = this.resolveProviderIds(user);

        this.googleAnalytics.gtag('set', {
            user_id: String(customer.id ?? user.uid ?? ''),
            user_properties: {
                app: 'cargomate-portal',
                environment: environment.production ? 'prod' : 'dev',
                firebase_uid: String(user.uid ?? ''),
                auth_provider: providerIds[0] ?? 'unknown',
                auth_provider_count: String(providerIds.length),
                customer_id: String(customer.id ?? ''),
                company_id: String(company?.id ?? ''),
                company_name: String(company?.name ?? ''),
                company_country_id: String(company?.countryId ?? ''),
                company_enterprise: String(!!company?.enableEnterpriseFeatures),
                corporate_role: String(customer.corporateRole ?? ''),
                role_names: roleNames.join('|'),
                role_count: String(roleNames.length),
                customer_country_id: String(customer.countryId ?? ''),
                is_phone_verified: String(!!customer.isPhoneVerified),
                has_phone: String(!!customer.phoneNumber),
                has_email: String(!!customer.emailAddress),
                email_domain: this.extractEmailDomain(customer.emailAddress ?? user.email ?? undefined),
            }
        });
    }

    private clearAnalyticsUserContext(): void {
        this.googleAnalytics.gtag('set', {
            user_id: null,
            user_properties: null
        });
    }

    private resolveRoleNames(customer: Customer, user: any): string[] {
        const customerRoles = Array.isArray(customer.roles)
            ? customer.roles.map((role: any) => role?.roleName ?? String(role)).filter(Boolean)
            : [];
        const sessionRoles = Array.isArray(user?.roles) ? user.roles.map((role: any) => String(role)).filter(Boolean) : [];

        return Array.from(new Set([...customerRoles, ...sessionRoles]));
    }

    private resolveProviderIds(user: any): string[] {
        return Array.isArray(user?.providerData)
            ? user.providerData
                .map((provider: any) => provider?.providerId)
                .filter((providerId: string | undefined): providerId is string => !!providerId)
            : [];
    }

    private extractEmailDomain(email?: string): string {
        if (!email || !email.includes('@')) {
            return '';
        }

        return email.split('@').pop() ?? '';
    }

    private refreshPwaInstallState(): void {
        const standalone = window.matchMedia('(display-mode: standalone)').matches
            || (window.navigator as any).standalone === true;

        this.isPwaInstalled = standalone;
        this.canInstallPwa = false;

        if (standalone) {
            this.deferredInstallPrompt = null;
        }
    }

    private refreshAppMenus(): void {
        const { profileMenuItems, toolbarMenuItems } = this.appMenuBuilderService.buildMenus(() => this.logout());
        this.profileMenuItems = profileMenuItems;
        this.toolbarMenuItems = toolbarMenuItems;
        this.appNavigationService.setMenuItems(this.toolbarMenuItems);
        this.menu.setItems(this.toolbarMenuItems);
    }

    onTenantScopeChanged(scope: TenantScope): void {
        if (scope.type === 'COMPANY') {
            this.syncSelectedCompanyDisplay(scope.companyId, scope.companyName);
        }

        this.refreshAppMenus();
        this.syncAnalyticsUserContext();
    }

    private bootstrapTenantAccess(user: any): void {
        const userUid = user?.uid ?? null;
        if (this.tenantAccessBootstrapUserUid === userUid) {
            return;
        }

        this.tenantAccessBootstrapUserUid = userUid;
        this.tenantScopeService.initializeCurrentAccess().pipe(take(1)).subscribe(() => {
            const activeScope = this.tenantScopeService.getActiveScope();
            if (activeScope?.type === 'COMPANY') {
                this.syncSelectedCompanyDisplay(activeScope.companyId, activeScope.companyName);
            } else {
                this.company = this.sessionStorageService.getCompany()
                    ?? this.sessionStorageService.getCustomer()?.company
                    ?? this.company;
            }
            this.enterpriseFeatureService.getAllSubscriptions().subscribe(() => {
                this.enterpriseFeatureService.refreshTrialLifecycle().subscribe(() => this.refreshAppMenus());
            });
            this.syncAnalyticsUserContext();
        });
    }

    private syncSelectedCompanyDisplay(companyId: number, fallbackName?: string): void {
        const storedCompany = this.sessionStorageService.getCompany();
        if (storedCompany?.id === companyId) {
            this.company = storedCompany;
            if (storedCompany.logo) {
                return;
            }
        }

        const customerCompany = this.customer?.company ?? this.sessionStorageService.getCustomer()?.company;
        if (customerCompany?.id === companyId) {
            this.company = customerCompany;
            this.sessionStorageService.saveCompany(customerCompany);
            if (customerCompany.logo) {
                return;
            }
        }

        this.company = {
            ...(storedCompany ?? {}),
            id: companyId,
            name: fallbackName ?? storedCompany?.name ?? 'Company'
        } as Company;
        this.sessionStorageService.saveCompany(this.company);

        this.companyService.getCompanyById(companyId).pipe(
            take(1),
            catchError(() => of(null))
        ).subscribe(companyWrapper => {
            const resolvedCompany = companyWrapper?.data;
            if (!resolvedCompany?.id || resolvedCompany.id !== companyId) {
                return;
            }

            this.company = resolvedCompany;
            this.sessionStorageService.saveCompany(resolvedCompany);
            this.syncAnalyticsUserContext();
        });
    }

    showTrialBanner(): boolean {
        const lifecycle = this.enterpriseFeatureService.trialLifecycle();
        return this.isLoggedIn && !!lifecycle && ['TRIAL_ACTIVE', 'TRIAL_EXPIRING']
            .includes((lifecycle.status ?? '').toUpperCase());
    }

    trialBannerClass(): string {
        const status = (this.enterpriseFeatureService.trialLifecycle()?.status ?? '').toUpperCase();
        if (status === 'TRIAL_EXPIRING') return 'warn';
        return 'active';
    }

    trialBannerTitle(): string {
        const lifecycle = this.enterpriseFeatureService.trialLifecycle();
        const status = (lifecycle?.status ?? '').toUpperCase();
        return this.localeService.translate('trial.daysRemaining', { days: lifecycle?.daysRemaining ?? (status === 'TRIAL_EXPIRING' ? 0 : 30) });
    }

    trialBannerMessage(): string {
        const lifecycle = this.enterpriseFeatureService.trialLifecycle();
        return this.localeService.translate('trial.activeMessage');
    }

    manageSubscription(): void {
        this.router.navigate(['/enterprise-subscription/manage']);
    }



    toggleProductivityLauncher(): void {
        this.productivityLauncherOpen = !this.productivityLauncherOpen;
    }

    openProductivity(route: string): void {
        this.productivityLauncherOpen = false;
        this.mobileSidebarVisible = false;
        this.router.navigate([route]);
    }

    productivityTotalCount(): number {
        return (this.productivitySummary.unreadNotifications || 0)
            + (this.productivitySummary.pendingTasks || 0)
            + (this.productivitySummary.pendingIncidents || 0);
    }

    private bootstrapProductivityTools(): void {
        if (!this.customer?.id || !this.company?.id) {
            return;
        }
        this.loadProductivitySummary();
        this.productivitySummarySubscription ??= this.productivityService.refreshSummary$.subscribe(() => this.loadProductivitySummary());
        this.productivityIntervalSubscription ??= interval(60000).subscribe(() => this.loadProductivitySummary());
        this.registerFirebaseMessaging();
    }

    private loadProductivitySummary(): void {
        if (!this.customer?.id || !this.company?.id) {
            return;
        }
        this.productivityService.getSummary(this.company.id, this.customer.id).subscribe({
            next: res => {
                this.productivitySummary = res.data || { unreadNotifications: 0, pendingTasks: 0, pendingIncidents: 0 };
            },
            error: () => {
                this.productivitySummary = { unreadNotifications: 0, pendingTasks: 0, pendingIncidents: 0 };
            }
        });
    }

    private registerFirebaseMessaging(): void {
        if (this.messagingRegistered || !this.messaging || !environment.firebaseVapidKey) {
            return;
        }
        this.messagingRegistered = true;
        Notification.requestPermission().then(permission => {
            if (permission !== 'granted' || !this.messaging) {
                return;
            }
            return runInInjectionContext(this.injector, () =>
                getToken(this.messaging!, { vapidKey: environment.firebaseVapidKey })
            );
        }).then(token => {
            if (token) {
                this.productivityService.registerDevice(token).subscribe();
            }
        }).catch(() => {
            this.messagingRegistered = false;
        });

        runInInjectionContext(this.injector, () => {
            onMessage(this.messaging!, payload => {
                this.messageService.add({
                    severity: 'info',
                    summary: payload.notification?.title || 'Notification',
                    detail: payload.notification?.body || ''
                });
                this.loadProductivitySummary();
            });
        });
    }

    toggleDesktopNav(): void {
        this.desktopNavExpanded = !this.desktopNavExpanded;
    }

    openDesktopNav(): void {
        this.desktopNavExpanded = true;
    }

    closeDesktopNavOnFocusOut(event: FocusEvent): void {
        const rail = event.currentTarget as HTMLElement;
        if (!rail.contains(event.relatedTarget as Node | null) && !rail.matches(':hover')) {
            this.closeDesktopNav();
        }
    }

    closeDesktopNav(): void {
        if (this.desktopNavExpanded && this.document.activeElement?.closest('.desktop-rail .nav-children')) {
            (this.document.querySelector('.desktop-rail .rail-toggle') as HTMLElement | null)?.focus();
        }
        this.desktopNavExpanded = false;
    }

    @HostListener('document:keydown.escape')
    closeDesktopNavOnEscape(): void {
        if (this.desktopNavExpanded && this.document.activeElement?.closest('.desktop-rail')) {
            (this.document.querySelector('.desktop-rail .rail-toggle') as HTMLElement | null)?.focus();
        }
        this.closeDesktopNav();
    }

    handleMenuNavigate(): void {
        this.mobileSidebarVisible = false;
        this.desktopNavExpanded = false;
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
    }

    navigateHome(event: MouseEvent): void {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) {
            return;
        }
        event.preventDefault();
        this.closeDesktopNav();
        this.mobileSidebarVisible = false;
        const normalized = normalizeUrl(this.currentUrl);
        if (normalized === '/dashboard' || normalized === '/') {
            this.appNavigationService.triggerHomeReset();
        } else {
            this.router.navigate(['/dashboard']);
        }
    }

    runMenuCommand(item: MenuItem, event?: Event): void {
        item.command?.({ originalEvent: event, item } as any);
        this.handleMenuNavigate();
    }

    /** Whether the AI Chat drawer button should be visible in the top bar. */
    get isAiChatDrawerAvailable(): boolean {
        const can = resolveAppMenuPermissions(this.permissionService);
        return this.enterpriseFeatureService.getIsAiChatSubscribed() && can.canViewAiChat;
    }

    /** Open the AI Chat drawer and populate the current route context. */
    openAiChatDrawer(): void {
        this.aiChatStateContext = this.resolveAiStateContext();
        this.aiChatDrawerVisible = true;
    }

    /** Build state context from the current route and active menu item. */
    private resolveAiStateContext(): AiStateContext {
        const customer = this.customer ?? this.sessionStorageService.getCustomer();
        const company = this.company ?? customer?.company;

        const activeItem = this.menu.activeItem();
        const moduleKey = this.resolveModuleFromUrl(this.currentUrl);

        return {
            routeUrl: this.currentUrl,
            pageTitle: activeItem?.label,
            module: moduleKey,
            companyId: company?.id,
            customerId: customer?.id
        };
    }

    /** Derive the module key from the current URL path. */
    private resolveModuleFromUrl(url: string): string {
        const normalized = normalizeUrl(url);
        const segments = normalized.split('/').filter(Boolean);
        if (segments.length > 0) {
            // Map common route prefixes to module keys
            const moduleMap: Record<string, string> = {
                'shipment': 'shipment',
                'fleet': 'fleet',
                'accounts': 'accounts',
                'finance': 'accounts',
                'order': 'order',
                'crm': 'crm',
                'clearance': 'clearance',
                'container-tracking': 'containertracking',
                'warehousing': 'warehousing',
                'transport-planning': 'transportplanning',
                'fuel-management': 'fuelmanagement',
                'gps-tracking': 'gpstracking',
                'posm': 'posm',
                'expense': 'expense',
                'hr': 'hr',
                'logistics-projects': 'logisticsproject',
                'reporting': 'reporting',
                'workflow': 'workflow',
                'audit': 'audit',
                'productivity': 'productivity',
                'company': 'company',
                'customer': 'customer',
                'dashboard': 'dashboard',
                'contract': 'contract',
                'ai-chat': 'ai',
                'wasender': 'wasender',
                'developer-platform': 'developerplatform',
                'business-events': 'businessevents'
            };
            return moduleMap[segments[0]] || segments[0];
        }
        return '';
    }

    companyInitial(): string {
        return (this.company?.name ?? 'C').trim().charAt(0).toUpperCase() || 'C';
    }

    private resolveGlobalSearch(query: string): Observable<GlobalSearchViewGroup[]> {
        const pageGroups = buildPageGlobalSearchGroups(this.toolbarMenuItems, query);
        if (!query || query.length < 2) {
            return of(pageGroups);
        }

        const activeScope = this.tenantScopeService.getActiveScope();
        const activeCompanyId = (activeScope?.type === 'COMPANY' ? activeScope.companyId : null)
            ?? this.company?.id
            ?? this.sessionStorageService.getCompany()?.id
            ?? null;

        return this.globalSearchService.search(query, 5, activeCompanyId).pipe(
            map(response => mergeGlobalSearchGroups(pageGroups, response.groups ?? [])),
            catchError(() => of(pageGroups))
        );
    }
}
